import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, Stack, Typography } from '@mui/material';

import useNotification from '../../../core/hooks/use_notification';
import { getAllPodsAction, getAllPodsState } from '../../../features/pod/get_all_pods';
import { getSalesSummaryAction, getSalesSummaryState } from '../../../features/stats/get_sales_summary';
import { getPodsNotificationData, getSalesStatsNotificationData } from '../../../core/utils/notification_utils';
import { buildStatsQueryParams, DateRangePresetEnum } from './sales_analysis_utils';
import SalesAnalysisFilters from './filters';
import KpiTiles from './kpi_tiles';
import SalesChart from './sales_chart';
import PodRankingChart from './pod_ranking_chart';
import TopProductsChart from './top_products_chart';
import CategoryChart from './category_chart';

const initialFilters = {
    preset: DateRangePresetEnum.LAST_30_DAYS.value,
    ...DateRangePresetEnum.LAST_30_DAYS.range(),
    podIds: []
};

const SalesAnalysisPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getAllPods = useSelector(getAllPodsState);
    const getSalesSummary = useSelector(getSalesSummaryState);

    const [filters, setFilters] = useState(initialFilters);

    const queryKey = JSON.stringify(buildStatsQueryParams(filters));
    // stable object identity per filter value, so charts refetch only when the filters really change
    const query = useMemo(() => JSON.parse(queryKey), [queryKey]);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    useEffect(() => {
        dispatch(getSalesSummaryAction(query))
            .then(response => showErrorMessage(getSalesStatsNotificationData(response)));
    }, [dispatch, query, showErrorMessage]);

    return (
        <Box mb={ 6 }>
            <Stack direction="column" spacing={ 0.5 } mb={ 3 }>
                <Typography variant="h1" sx={ { m: 0 } }>
                    Analiza sprzedaży
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={ { fontWeight: 500 } }>
                    Sprzedaż produktów w punktach sprzedaży i rezerwacjach
                </Typography>
            </Stack>

            <Stack spacing={ 3 }>
                <Box>
                    <SalesAnalysisFilters filters={ filters } setFilters={ setFilters } pods={ getAllPods?.data }/>
                    {
                        filters.podIds.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={ { display: 'block', mt: 1 } }>
                                Rezerwacje nie są przypisane do punktów sprzedaży, dlatego przy wybranych punktach nie są uwzględniane.
                            </Typography>
                        )
                    }
                </Box>

                <KpiTiles summary={ getSalesSummary?.data } loading={ getSalesSummary?.loading }/>

                <SalesChart query={ query }/>

                {/* Grid is wrapped, Stack spacing would otherwise reset its negative margins and shift the cards */ }
                <Box>
                    <Grid container spacing={ 3 }>
                        <Grid item xs={ 12 } lg={ 6 }>
                            <TopProductsChart query={ query }/>
                        </Grid>
                        <Grid item xs={ 12 } lg={ 6 }>
                            <CategoryChart query={ query }/>
                        </Grid>
                    </Grid>
                </Box>

                <PodRankingChart query={ query } pods={ getAllPods?.data }/>
            </Stack>
        </Box>
    );
};

export default SalesAnalysisPage;
